import { http } from '../client'
import { USE_MOCKS } from '../config'
import { endpoints } from '../endpoints'
import { toSummary } from '../mock/fixtures'
import { delay, makeId, mockDb } from '../mock/db'
import { ApiError } from '../types'
import type { CreatePurchasePayload, Purchase } from '../types'

/**
 * Compras que o usuário marcou como feitas. É o que alimenta a página
 * "Já comprados" e o contador da sacola no cabeçalho.
 */

export function listPurchases(): Promise<Purchase[]> {
  if (!USE_MOCKS) return http.get<Purchase[]>(endpoints.purchases.list)
  return delay(mockDb.state.purchases, 260)
}

export function createPurchase(payload: CreatePurchasePayload): Promise<Purchase> {
  if (!USE_MOCKS) return http.post<Purchase>(endpoints.purchases.create, payload)

  if (!mockDb.state.user) {
    return Promise.reject(
      new ApiError(
        'Você precisa entrar na sua conta para adicionar produtos comprados.',
        401,
        'unauthorized',
      ),
    )
  }

  const catalog = mockDb.allProducts()
  const product = payload.productId
    ? catalog.find((p) => p.id === payload.productId)
    : catalog.find((p) => p.externalUrl === payload.productUrl) ?? catalog[0]

  if (!product) {
    return Promise.reject(
      new ApiError('Não reconhecemos esse link de produto.', 422, 'invalid_product_url'),
    )
  }

  const purchase: Purchase = {
    id: makeId('pur'),
    product: toSummary(product),
    purchasedAt: new Date().toISOString(),
    couponCode: makeId('CUPOM').toUpperCase(),
  }

  mockDb.update({ purchases: [purchase, ...mockDb.state.purchases] })
  return delay(purchase, 450)
}

export function removePurchase(id: string): Promise<void> {
  if (!USE_MOCKS) return http.delete<void>(endpoints.purchases.remove(id))

  mockDb.update({
    purchases: mockDb.state.purchases.filter((p) => p.id !== id),
  })
  return delay(undefined, 240)
}

/** Filtra os últimos 7 dias — regra usada na tela "comprados última semana". */
export function filterLastWeek(purchases: Purchase[]): Purchase[] {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000
  return purchases.filter((p) => new Date(p.purchasedAt).getTime() >= cutoff)
}
